
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useDrawingLoader(onLoadDrawing: (data: string, drawingId: string, analysisData?: any) => void) {
  const [loadingDrawingId, setLoadingDrawingId] = useState<string | null>(null);

  const fetchFullDrawingData = async (id: string) => {
    try {
      console.log('Fetching full drawing data with analysis for ID:', id);
      const { data, error } = await supabase
        .from("whiteboard_history")
        .select(`
          drawing_data,
          version,
          drawing_analyses (*)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      console.log('Drawing data fetched with version:', data?.version);
      console.log('Analysis data:', data?.drawing_analyses);
      return data;
    } catch (error) {
      console.error("Error fetching full drawing data:", error);
      throw error;
    }
  };

  const handleLoad = async (id: string) => {
    console.log('Loading drawing with ID:', id);
    setLoadingDrawingId(id);
    try {
      document.body.style.pointerEvents = 'none';
      const fullData = await fetchFullDrawingData(id);
      if (fullData?.drawing_data) {
        console.log('Successfully fetched drawing data, passing to parent');
        document.body.style.pointerEvents = 'auto';
        const analysisData = fullData.drawing_analyses?.[0];
        console.log('Analysis data to pass:', analysisData);
        
        // Track when we're loading the drawing with analysis
        if (analysisData?.analysis_text) {
          console.log('Drawing has analysis text:', analysisData.analysis_text.substring(0, 50) + '...');
        }
        
        // Ensure version information is properly included
        try {
          const drawingDataObj = JSON.parse(fullData.drawing_data);
          // Add version to drawing data if missing from source but available in record
          if (!drawingDataObj.version && fullData.version) {
            console.log('Adding version to drawing data:', fullData.version);
            drawingDataObj.version = fullData.version;
            fullData.drawing_data = JSON.stringify(drawingDataObj);
          }
          onLoadDrawing(fullData.drawing_data, id, analysisData);
        } catch (parseError) {
          console.error('Error parsing drawing data:', parseError);
          toast({
            variant: "destructive",
            title: "Invalid drawing data",
            description: "The drawing data could not be parsed.",
          });
        }
      }
    } catch (error) {
      console.error('Error in handleLoad:', error);
      toast({
        variant: "destructive",
        title: "Error loading drawing",
        description: "There was a problem loading your drawing.",
      });
    } finally {
      setTimeout(() => {
        setLoadingDrawingId(null);
        document.body.style.pointerEvents = 'auto';
      }, 1000);
    }
  };

  return {
    loadingDrawingId,
    handleLoad
  };
}
